import * as yup from 'yup';
import { SpecialTerm, SubscriptionTier } from '../../../../enums/clientEnums';
import { currentTermYears, termYears } from './subscriptionTerms';
import type { ClientDetails, ClientFormValues, ClientPayload, ClientUpdatePayload, PaymentGroupSelection, SubscriptionType } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

export const PHONE_MAX_DIGITS = 10;

// Demo is built and validated; the option is simply not offered yet.
export const SHOW_DEMO_OPTION = false;

// A tier can be upgraded but never downgraded before it expires.
export const TIER_RANK: Record<SubscriptionTier, number> = {
  [SubscriptionTier.DEMO]: 0,
  [SubscriptionTier.BASIC]: 1,
  [SubscriptionTier.PREMIUM]: 2,
};

export const FIELD_LABELS = {
  CLIENT_NAME: 'CLIENT NAME',
  ADMIN_NAME: 'ADMINISTRATOR NAME',
  ADMIN_EMAIL: 'ADMINISTRATOR EMAIL',
  ADMIN_PHONE: 'ADMINISTRATOR PHONE',
  SUBSCRIPTION_TYPE: 'SUBSCRIPTION TYPE',
  SUBSCRIPTION_LENGTH: 'SUBSCRIPTION LENGTH',
  ADD_ONS: 'ADD-ONS',
  ADD_ON_LENGTH: 'ADD-ON LENGTH',
  PAYMENT_GROUP: 'GROUP',
  CONTACT_NAME: 'CONTACT NAME',
  CONTACT_EMAIL: 'CONTACT EMAIL',
  CONTACT_PHONE: 'CONTACT PHONE',
  PAYMENT_METHOD: 'PAYMENT METHOD',
  MARK_AS_PAID: 'MARK AS PAID',
  PAYMENT_STATUS: 'PAYMENT STATUS',
};

export const FIELD_PLACEHOLDERS = {
  CLIENT_NAME: 'Enter client name.',
  ADMIN_NAME: "Please enter the client administrator's name.",
  ADMIN_EMAIL: "Please enter the client administrator's email.",
  ADMIN_PHONE: "Please enter the client administrator's phone.",
  SUBSCRIPTION_TYPE: 'Please select the subscription level.',
  SUBSCRIPTION_LENGTH: 'Please select the subscription length.',
  ADD_ON_LENGTH: 'Select a length.',
  RENEW_ADD_ON: 'Expired - select a length to renew.',
  PAYMENT_GROUP: 'Please enter the name of the organization.',
  CONTACT_NAME: 'Please enter a contact name for the organization.',
  CONTACT_EMAIL: 'Please enter a contact email.',
  CONTACT_PHONE: 'Please enter a contact phone number.',
  PAYMENT_METHOD: 'Please record a payment method.',
};

export const STEP_HEADINGS = {
  CLIENT_INFORMATION: 'CLIENT INFORMATION',
  CLIENT_ADMINISTRATOR: 'CLIENT ADMINISTRATOR',
  SUBSCRIPTION_INFORMATION: 'SUBSCRIPTION INFORMATION',
  PAYMENT_INFORMATION: 'PAYMENT INFORMATION',
  SUBSCRIPTION_SUMMARY: 'SUBSCRIPTION SUMMARY',
  // The summary's section headers are the tab names, not the step headings above.
  CLIENT_INFORMATION_SHORT: 'CLIENT',
  SUBSCRIPTION_INFORMATION_SHORT: 'SUBSCRIPTION',
  PAYMENT_INFORMATION_SHORT: 'PAYMENT',
};

export const STORAGE_LENGTH_COPY = 'Tied to subscription term';
export const ADD_ONS_EMPTY_COPY = 'No add-ons yet.';
export const ADD_ONS_ERROR_COPY = 'Unable to load add-ons. Please try again.';

export const administratorPopover = (
  <div className={styles.popoverContent}>
    <span>
      The client administrator is the admin responsible for the client account, and is able to set up new users and permission levels. This can be
      changed later on.
    </span>
  </div>
);

export const subscriptionLengthPopover = (
  <div className={styles.popoverContent}>
    <span>Select the duration of the subscription. Subscriptions can be set in yearly increments.</span>
  </div>
);

const planAllowances = ({ features }: SubscriptionType) =>
  `${features.maps} maps, ${features.storageGB} GB storage, ${features.admins} administrators, ${features.editors} editors, and ${features.viewers} viewers`;

// Built from the plan catalogue so prices and allowances can't drift from the backend.
export const subscriptionTypePopover = (subscriptionTypes: SubscriptionType[]) => (
  <div className={styles.popoverContent}>
    <span>Subscription levels and what each includes.</span>
    <ul className={styles.popoverList}>
      {subscriptionTypes.map((type) => (
        <li key={type.id}>
          {type.name}: ${type.price.toFixed(2)} — {planAllowances(type)}.
        </li>
      ))}
    </ul>
  </div>
);

export const paymentGroupPopover = (
  <div className={styles.popoverContent}>
    <span>
      If the payment is being billed to an external organization (that is not the client), the information can be entered here. If the billing is
      being done directly to the client, the client can be entered as the group.
    </span>
  </div>
);

export const paymentMethodPopover = (
  <div className={styles.popoverContent}>
    <span>Payment Method is currently for internal recording purposes only.</span>
  </div>
);

export const addOnsPopover = (
  <div className={styles.popoverContent}>
    <span>Add-Ons are in addition to the base subscription.</span>
    <ul className={styles.popoverList}>
      <li>Additional Storage: Allows the client additional store on their account.</li>
      <li>Additional Maps: Allows the client to create additional maps</li>
      <li>Additional Admins: Allows the client to have additional admins</li>
      <li>Additional Data Editors: Allows the client to have additional editors</li>
      <li>3D Viewer: Allows 3D Map access to the user</li>
      <li>MARS: Access to the Mining Activity Reporting Service page.</li>
      <li>Infrastructure Manager: Access to work orders and infrastructure manager.</li>
      <li>Emergency Dashboard: Access to live emergency notifications including flooding, fires, and boil water advisories.</li>
    </ul>
  </div>
);

export const CLIENT_FORM_DEFAULTS: ClientFormValues = {
  name: '',
  adminName: '',
  adminEmail: '',
  adminPhone: '',
  subscriptionType: '',
  subscriptionTerm: '',
  addOns: [],
  paymentGroupId: null,
  paymentGroupName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  paymentMethod: '',
  markAsPaid: false,
};

// Terms map to CURRENT, never a year count: re-deriving one would re-date an untouched subscription.
// The caller resolves `paymentGroupName`; the payload names the group by id alone.
// Seed/legacy clients can have admin, subscription, or paymentInfo null outright;
// each falls back to its CLIENT_FORM_DEFAULTS value so the wizard still opens.
export const detailsToClientFormValues = (details: ClientDetails, paymentGroupName: string): ClientFormValues => ({
  name: details.client.clientName,
  adminName: details.admin?.name ?? '',
  adminEmail: details.admin?.email ?? '',
  adminPhone: details.admin?.phone ?? '',
  subscriptionType: details.subscription?.type ?? '',
  subscriptionTerm: details.subscription?.expiresAt ? SpecialTerm.CURRENT : '',
  addOns: details.addons.map((addOn) => ({
    addonId: addOn.addonId,
    quantity: addOn.quantity,
    // Untouched: the saved length is resent from heldYears, so no term is picked yet.
    term: null,
    heldQuantity: addOn.quantity,
    isExpired: addOn.isExpired ?? false,
    heldYears: addOn.addonLength ?? null,
    expiresAt: addOn.expiresAt,
    renew: false,
  })),
  paymentGroupId: details.client.paymentGroupId,
  paymentGroupName,
  contactName: details.paymentInfo?.contactName ?? '',
  contactEmail: details.paymentInfo?.contactEmail ?? '',
  contactPhone: details.paymentInfo?.contactPhone ?? '',
  paymentMethod: details.paymentInfo?.paymentMethod ?? '',
  markAsPaid: details.subscription?.markedAsPaid ?? false,
});

// A picked length goes out as picked (Current ends with the subscription); an untouched saved row is resent as received.
const addOnLengthPayload = (addOn: ClientFormValues['addOns'][number]) => {
  if (addOn.term === SpecialTerm.CURRENT || addOn.term === SpecialTerm.DEMO) return { untilSubscriptionEnd: true as const };
  if (addOn.term) return { addonLength: Number(addOn.term) };
  return addOn.heldYears !== null ? { addonLength: addOn.heldYears } : {};
};

// Always the whole list: the backend deletes an expired add-on that is left out, so this must never become a diff.
const mapAddOnsToPayload = (addOns: ClientFormValues['addOns']) =>
  addOns.map((addOn) => {
    // Renewing needs a length alongside the flag.
    const renew = addOn.isExpired && addOn.renew && !!addOn.term;
    return { addonId: addOn.addonId, quantity: addOn.quantity, ...addOnLengthPayload(addOn), ...(renew ? { renew: true as const } : {}) };
  });

const buildPaymentGroupSelection = (values: ClientFormValues): PaymentGroupSelection =>
  values.paymentGroupId ? { paymentGroupId: values.paymentGroupId } : { paymentGroupName: values.paymentGroupName.trim() };

export const formValuesToClientPayload = (values: ClientFormValues): ClientPayload => ({
  client: {
    clientName: values.name.trim(),
    administratorName: values.adminName.trim(),
    administratorEmail: values.adminEmail.trim(),
    administratorPhone: values.adminPhone.trim(),
  },
  subscription: {
    subscriptionType: values.subscriptionType,
    subscriptionLength: values.subscriptionTerm ? termYears(values.subscriptionTerm) : null,
    addons: mapAddOnsToPayload(values.addOns),
  },
  payment: {
    ...buildPaymentGroupSelection(values),
    contactName: values.contactName.trim(),
    contactEmail: values.contactEmail.trim(),
    contactPhone: values.contactPhone.trim(),
    paymentMethod: values.paymentMethod.trim(),
    markAsPaid: values.markAsPaid,
  },
});

// PUT acts like PATCH: omit unchanged keys instead of sending null/''.
export const formValuesToClientUpdatePayload = (
  values: ClientFormValues,
  dirtyFields: Partial<Record<keyof ClientFormValues, unknown>>,
  currentSubscription: { startsAt: string | null; expiresAt: string | null } | null,
): ClientUpdatePayload => {
  const client: ClientUpdatePayload['client'] = {};
  if (dirtyFields.name) client.clientName = values.name.trim();
  if (dirtyFields.adminName) client.administratorName = values.adminName.trim();
  if (dirtyFields.adminEmail) client.administratorEmail = values.adminEmail.trim();
  if (dirtyFields.adminPhone) client.administratorPhone = values.adminPhone.trim();

  const payment: ClientUpdatePayload['payment'] = {};
  if (dirtyFields.paymentGroupName) Object.assign(payment, buildPaymentGroupSelection(values));
  if (dirtyFields.contactName) payment.contactName = values.contactName.trim();
  if (dirtyFields.contactEmail) payment.contactEmail = values.contactEmail.trim();
  if (dirtyFields.contactPhone) payment.contactPhone = values.contactPhone.trim();
  if (dirtyFields.paymentMethod) payment.paymentMethod = values.paymentMethod.trim();
  if (dirtyFields.markAsPaid) payment.markAsPaid = values.markAsPaid;

  const subscriptionChanged = !!(dirtyFields.subscriptionType || dirtyFields.subscriptionTerm || dirtyFields.addOns);

  return {
    ...(Object.keys(client).length ? { client } : {}),
    ...(subscriptionChanged
      ? {
          subscription: {
            subscriptionType: values.subscriptionType,
            subscriptionLength:
              values.subscriptionTerm === SpecialTerm.CURRENT
                ? currentTermYears(currentSubscription?.startsAt, currentSubscription?.expiresAt)
                : values.subscriptionTerm
                  ? termYears(values.subscriptionTerm)
                  : null,
            addons: mapAddOnsToPayload(values.addOns),
          },
        }
      : {}),
    ...(Object.keys(payment).length ? { payment } : {}),
  };
};

// Fields the edit flow locks; the step already asks, so switching one on is an entry here.
export const EDIT_LOCKED_FIELDS = new Set<keyof ClientFormValues>();

export const clientFormValidation = yup.object({
  name: yup.string().trim().required('Client name is required'),
  adminName: yup.string().trim().required("Administrator's name is required"),
  adminEmail: yup.string().trim().required("Administrator's email is required").email('Enter a valid email address'),
  // Optional, but a partial number is worse than none.
  adminPhone: yup
    .string()
    .trim()
    .matches(new RegExp(`^\\d{${PHONE_MAX_DIGITS}}$`), {
      message: `Enter a ${PHONE_MAX_DIGITS} digit phone number`,
      excludeEmptyString: true,
    }),
  subscriptionType: yup.string().required('Subscription type is required'),
  subscriptionTerm: yup.string().required('Subscription length is required'),
  // A safety net: the step sets a length on every row that needs one.
  addOns: yup.array().of(
    yup
      .object({
        addonId: yup.number().required(),
        quantity: yup.number().required(),
        term: yup.string().nullable(),
        heldQuantity: yup.number().required(),
        isExpired: yup.boolean().required(),
        heldYears: yup.number().nullable(),
        expiresAt: yup.string().nullable(),
        renew: yup.boolean().required(),
      })
      // Any count change on an expired add-on is a renewal, so it needs a chosen length.
      .test(
        'expired-count-change-length',
        'Choose a length to renew the expired add-on',
        (row) => !row.isExpired || row.quantity === row.heldQuantity || (row.renew && !!row.term),
      ),
  ),
  paymentGroupId: yup.number().nullable(),
  paymentGroupName: yup.string().trim().required('A payment group is required'),
  contactName: yup.string().trim().required('Contact name is required'),
  contactEmail: yup.string().trim().required('Contact email is required').email('Enter a valid email address'),
  contactPhone: yup
    .string()
    .trim()
    .matches(new RegExp(`^\\d{${PHONE_MAX_DIGITS}}$`), {
      message: `Enter a ${PHONE_MAX_DIGITS} digit phone number`,
      excludeEmptyString: true,
    }),
  paymentMethod: yup.string().trim().required('Payment method is required'),
  // Whether the payment is confirmed, not whether the field was touched -- unchecked is a valid, honest state.
  markAsPaid: yup.boolean().required(),
});
